/**
 * Native Bridge
 * The single place the web layer and the iOS app agree on. Everything the app
 * needs to implement is described here.
 *
 * JS -> native:  window.webkit.messageHandlers.purchase.postMessage({ type, ... })
 * native -> JS:  window.totalTen.<callback>(...)
 *
 * Outside the app (plain web) there is no bridge, so every send is a no-op that
 * returns false and the caller falls back.
 */

export const PRODUCT_ID = 'io.totalten.unlimited';

// Native replies are re-broadcast as window events so any component can listen
export const PRICE_EVENT = 'totalten:price';
export const UNLIMITED_EVENT = 'totalten:unlimited';
export const PURCHASE_FAILED_EVENT = 'totalten:purchasefailed';
export const HISTORY_EVENT = 'totalten:history';

/**
 * Whether we are running inside the iOS app with the handler installed
 * @returns {boolean}
 */
export function isBridgeAvailable() {
  return Boolean(window.webkit?.messageHandlers?.purchase);
}

/**
 * Post to one named message handler. Each handler is installed independently,
 * so a build of the app that predates one of them simply never receives those
 * messages rather than throwing.
 * @param {string} handlerName
 * @param {*} payload
 * @returns {boolean} - false when there is no native side listening
 */
function postTo(handlerName, payload) {
  const handler = window.webkit?.messageHandlers?.[handlerName];
  if (!handler) return false;
  try {
    handler.postMessage(payload);
    return true;
  } catch (error) {
    console.error(`Error posting to native bridge handler "${handlerName}":`, error);
    return false;
  }
}

const postToNative = (payload) => postTo('purchase', payload);

/** Ask for the localized StoreKit display price. Answered by setPrice. */
export function requestPrice() {
  return postToNative({ type: 'getPrice', productId: PRODUCT_ID });
}

/** Begin the StoreKit purchase. Answered by setUnlimited or purchaseFailed. */
export function requestPurchase() {
  return postToNative({ type: 'purchase', productId: PRODUCT_ID });
}

/** Restore a previous purchase. Answered by setUnlimited or purchaseFailed. */
export function requestRestore() {
  return postToNative({ type: 'restore', productId: PRODUCT_ID });
}

/** Ask what StoreKit currently says we own. Answered by setUnlimited. */
export function requestEntitlement() {
  return postToNative({ type: 'getEntitlement', productId: PRODUCT_ID });
}

/**
 * Fire-and-forget board lifecycle pings on the app's existing `gameEvent`
 * handler. Bare strings, matching the "gameCompleted" the app already handles.
 * @param {string} event - 'gameStarted' | 'gameCompleted'
 * @returns {boolean} - false when there is no native side listening
 */
export function notifyGameEvent(event) {
  return postTo('gameEvent', event);
}

/**
 * Whether the app installed the iCloud history handler. Older builds of the app
 * did not, and the website never will - both fall back to local-only history.
 * @returns {boolean}
 */
export function isHistorySyncAvailable() {
  return Boolean(window.webkit?.messageHandlers?.historySync);
}

/** Ask for whatever iCloud currently holds. Answered by mergeHistory. */
export function requestHistoryPull() {
  return postTo('historySync', { type: 'pull' });
}

/**
 * Hand the whole history to native to write into iCloud.
 * @param {Object} shards - { h0: '<json>', h7: '<json>', ... }, opaque to native
 * @returns {boolean}
 */
export function pushHistoryShards(shards) {
  return postTo('historySync', { type: 'push', shards });
}

const emit = (name, detail) => window.dispatchEvent(new CustomEvent(name, { detail }));

/**
 * The callbacks native invokes, e.g.
 *   webView.evaluateJavaScript("window.totalTen.setPrice('$3.99')")
 */
window.totalTen = {
  ...(window.totalTen || {}),

  /** @param {string} displayPrice - already localized and formatted by StoreKit */
  setPrice: (displayPrice) => emit(PRICE_EVENT, String(displayPrice)),

  /** @param {boolean} owned - true once the purchase is verified */
  setUnlimited: (owned) => emit(UNLIMITED_EVENT, owned === true || owned === 'true'),

  /** @param {string} [reason] - omitted or empty when the user simply cancelled */
  purchaseFailed: (reason) => emit(PURCHASE_FAILED_EVENT, reason ? String(reason) : null),

  /**
   * Everything iCloud holds, after a pull or when another device writes.
   * Re-broadcast rather than applied here - historySync.js owns what a merge
   * means, and this file stays nothing but the protocol.
   * @param {string|Object} shards - the shard map, JSON string or object
   */
  mergeHistory: (shards) => emit(HISTORY_EVENT, shards),
};
